import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const emptyForm = {
  slug: "", name: "", tagline: "", description: "",
  price_inr: 0, compare_at_price_inr: null,
  images: [], category: "anarkali", occasion: [], craft: [],
  fabric: "", color: "", care: "", model_note: "", size_chart: "",
  variants: [{ size: "S", stock: 5 }, { size: "M", stock: 5 }, { size: "L", stock: 5 }],
  featured: false, active: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const { data } = await api.get("/admin/products");
    setProducts(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({
      slug: p.slug, name: p.name, tagline: p.tagline || "", description: p.description,
      price_inr: p.price_inr, compare_at_price_inr: p.compare_at_price_inr,
      images: p.images || [], category: p.category, occasion: p.occasion || [], craft: p.craft || [],
      fabric: p.fabric || "", color: p.color || "", care: p.care || "", model_note: p.model_note || "",
      size_chart: p.size_chart || "", variants: p.variants || [],
      featured: !!p.featured, active: !!p.active,
    });
    setEditingId(p.product_id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price_inr: parseFloat(form.price_inr) };
      if (payload.compare_at_price_inr) payload.compare_at_price_inr = parseFloat(payload.compare_at_price_inr);
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added.");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Catalog</p>
          <h1 className="font-serif text-4xl">Products</h1>
        </div>
        <button onClick={openNew} className="bg-[#5C1A1B] text-[#FDFBF7] px-4 py-2 text-[11px] uppercase tracking-nav hover:bg-[#701C1D] transition-colors flex items-center gap-2" data-testid="admin-products-new">
          <Plus size={14} /> New Product
        </button>
      </div>

      <div className="bg-white border border-black/5">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-nav text-[#7A7A7A] border-b border-black/5">
            <tr>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Featured</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_id} className="border-b border-black/5" data-testid={`admin-prod-row-${p.slug}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  <img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover" />
                  <div>
                    <p className="font-serif text-base">{p.name}</p>
                    <p className="text-[11px] text-[#7A7A7A] font-mono-alt">{p.slug}</p>
                  </div>
                </td>
                <td className="px-4 py-3">{p.category}</td>
                <td className="px-4 py-3 font-mono-alt">₹{Math.round(p.price_inr).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">{p.featured ? "Yes" : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} className="text-[11px] uppercase tracking-nav text-[#5C1A1B] hover:underline mr-4" data-testid={`admin-prod-edit-${p.slug}`}>Edit</button>
                  <button onClick={() => del(p.product_id)} className="text-[11px] uppercase tracking-nav text-[#D32F2F] hover:underline" data-testid={`admin-prod-del-${p.slug}`}><Trash2 size={12} className="inline" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-prod-modal">
          <div className="bg-white max-w-3xl w-full my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
              <p className="font-serif text-2xl">{editingId ? "Edit product" : "New product"}</p>
              <button onClick={() => setShowForm(false)}><X /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Name</span>
                  <input required className="w-full border border-black/10 px-3 py-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Slug</span>
                  <input required className="w-full border border-black/10 px-3 py-2 font-mono-alt" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-nav">Tagline</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-nav">Description</span>
                  <textarea rows="4" className="w-full border border-black/10 px-3 py-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Price (INR)</span>
                  <input required type="number" className="w-full border border-black/10 px-3 py-2" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Compare-at (INR, optional)</span>
                  <input type="number" className="w-full border border-black/10 px-3 py-2" value={form.compare_at_price_inr || ""} onChange={(e) => setForm({ ...form, compare_at_price_inr: e.target.value || null })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Category</span>
                  <select className="w-full border border-black/10 px-3 py-2" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {["anarkali","lehenga","saree","suit","kaftan","co-ord"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Occasion (comma)</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.occasion.join(",")} onChange={(e) => setForm({ ...form, occasion: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Craft (comma)</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.craft.join(",")} onChange={(e) => setForm({ ...form, craft: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Fabric</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Color</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-nav">Care</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.care} onChange={(e) => setForm({ ...form, care: e.target.value })} /></label>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-nav mb-2">Images</p>
                <ImageUploader
                  kind="product"
                  max={8}
                  value={form.images}
                  onChange={(arr) => setForm({ ...form, images: arr })}
                  testIdPrefix="admin-prod-img"
                />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-nav mb-2">Variants (size, stock)</p>
                {form.variants.map((v, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input value={v.size} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, size: e.target.value } : x) })} className="w-32 border border-black/10 px-3 py-2" />
                    <input type="number" value={v.stock} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, stock: parseInt(e.target.value || 0) } : x) })} className="w-24 border border-black/10 px-3 py-2" />
                    <button type="button" onClick={() => setForm({ ...form, variants: form.variants.filter((_, j) => j !== i) })} className="px-3 text-[#D32F2F] text-xs uppercase tracking-nav">Remove</button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, variants: [...form.variants, { size: "", stock: 0 }] })} className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">+ Variant</button>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-black/10">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-[11px] uppercase tracking-nav">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#5C1A1B] text-white text-[11px] uppercase tracking-nav" data-testid="admin-prod-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
